import type { WorkspaceMandateSummary } from '../../state/session.ts';
// The canonical rules-of-engagement ceiling, lowest to highest. Mirrors the backend TestingLevel.
export const TESTING_LEVELS = ['PASSIVE', 'ACTIVE', 'INTRUSIVE', 'HIGH_IMPACT'] as const;
export type TestingLevel = (typeof TESTING_LEVELS)[number];
export const testingLevelRank = (level: TestingLevel): number => TESTING_LEVELS.indexOf(level);
export const TESTING_LEVEL_NAMES: Record<TestingLevel, string> = {
  PASSIVE: 'Passive assessment',
  ACTIVE: 'Bounded active assessment',
  INTRUSIVE: 'Intrusive assessment',
  HIGH_IMPACT: 'High-impact assessment',
};
export const TESTING_LEVEL_SHORT: Record<TestingLevel, string> = {
  PASSIVE: 'Passive',
  ACTIVE: 'Active',
  INTRUSIVE: 'Intrusive',
  HIGH_IMPACT: 'High-impact',
};
// Requested per-level limits for the letter, mirroring the backend profiles. The backend
// re-derives and validates the permit rate against these, so they are the letter's ceiling.
export const TESTING_LEVEL_LIMITS: Record<
  TestingLevel,
  { requestsPerSecond: number; concurrency: number }
> = {
  PASSIVE: { requestsPerSecond: 1, concurrency: 1 },
  ACTIVE: { requestsPerSecond: 2, concurrency: 2 },
  INTRUSIVE: { requestsPerSecond: 4, concurrency: 4 },
  HIGH_IMPACT: { requestsPerSecond: 1000, concurrency: 8 },
};

export interface AuthorizationInput {
  engagement: string;
  name: string;
  email: string;
  role: string;
  authority: string;
  domains: string[];
  exclusions: string[];
  ipAddresses?: string[];
  testingLevel: TestingLevel;
  startsAt: string;
  endsAt: string;
  timezone: string;
  exclusive: boolean;
  policyEndsAt: string;
  ownershipDeclared: boolean;
  publicDisclosureApproved: boolean;
  /** Required for HIGH_IMPACT: the owner's written acceptance of possible disruption. */
  impactAccepted?: boolean;
}
export type AuthorizerField = 'engagement' | 'name' | 'role' | 'authority';
export function authorizerErrors(input: Pick<AuthorizationInput, AuthorizerField>) {
  const limits: [AuthorizerField, string, number, number][] = [
    ['engagement', 'Authorization name', 2, 80],
    ['name', 'Full name', 2, 100],
    ['role', 'Role', 2, 100],
    ['authority', 'Basis of authority', 5, 300],
  ];
  const errors: Partial<Record<AuthorizerField, string>> = {};
  for (const [field, label, min, max] of limits) {
    const value = input[field].trim();
    if (value.length < min || value.length > max || [...value].some((c) => c.charCodeAt(0) < 32))
      errors[field] = `${label} must contain ${min}–${max} characters without control characters.`;
  }
  return errors;
}

export interface Terms {
  organization: string;
  domain: string;
  engagement: string;
  authorizer: { name: string; email: string; role: string; authority: string };
  domains: string[];
  exclusions: string[];
  ipAddresses?: string[];
  testingLevel: TestingLevel;
  startsAt: string;
  endsAt: string;
  timezone: string;
  exclusive: boolean;
  policyEndsAt: string;
  limits: { requestsPerSecond: number; concurrency: number; prohibited: string[] };
  impactAccepted?: boolean;
}
export interface Letter {
  id: string;
  workspaceId: string;
  emailSentAt?: string | null;
  emailCodeAvailable?: boolean;
  terms: Terms;
  termsHash: string;
  createdAt: string;
  confirmedAt: string | null;
  revokedAt: string | null;
  status: string;
  proof: { status: string; checkedAt: string | null };
  /** What the owner adds to their own zone once approved: the signed letter TXT and page CNAME. */
  records?: { type: 'TXT' | 'CNAME'; name: string; value: string }[];
  /** The authorization page on the customer's own domain, once its CNAME is published. */
  pageUrl?: string | null;
}
export interface AuthorizationConfig {
  available: boolean;
  grantMaxHours: number;
  confirmationMinutes: number;
  issuer: string;
  profiles: { id: TestingLevel; name: string; description: string; prohibited?: string[] }[];
}
// The signed letter a customer publishes at _traceira-authz.<domain> TXT. Traceira keeps no
// public copy: the backend only reads it from the customer's DNS and returns it as found.
export interface DomainLetter {
  version: string;
  issuer: string;
  audience: string;
  grantId: string;
  authorizedOperator: string;
  domain: string;
  domains: string[];
  ipAddresses?: string[];
  exclusions: string[];
  testingLevel: string;
  startsAt: string;
  endsAt: string;
  confirmedAt: string;
  termsHash: string;
  jws: string;
}
export interface DomainLetterResult {
  domain: string;
  authorized: boolean;
  letter: DomainLetter | null;
  reasons: string[];
}
/** The window status the signed letter itself states; revocation is removing the record. */
export const letterStatus = (letter: DomainLetter, now = Date.now()) =>
  now < Date.parse(letter.startsAt)
    ? 'scheduled'
    : now >= Date.parse(letter.endsAt)
      ? 'expired'
      : 'active';
/** The mandate status now: the server summary is read once, and a window can end mid-session. */
export const mandateStatus = (mandate?: WorkspaceMandateSummary) =>
  mandate?.status === 'active' && mandate.endsAt && Date.parse(mandate.endsAt) <= Date.now()
    ? 'expired'
    : mandate?.status;

export const date = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZoneName: undefined,
  }).format(new Date(value));
export const localDate = (value: Date) =>
  new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
export const hosts = (value: string) => value.split(/[\s,]+/).filter(Boolean);
export const domainScopeDomains = (domain: string) => (domain ? [domain, `*.${domain}`] : []);
export const message = (error: unknown) =>
  error instanceof Error ? error.message : 'This request failed. Please try again.';
export function saveOwner(id: string, token: string) {
  try {
    localStorage.setItem(`traceira-owner-${id}`, token);
    sessionStorage.setItem(`traceira-owner-${id}`, token);
  } catch {
    /* fallback */
  }
}
export function readOwner(id: string) {
  try {
    return (
      localStorage.getItem(`traceira-owner-${id}`) ??
      sessionStorage.getItem(`traceira-owner-${id}`) ??
      ''
    );
  } catch {
    return '';
  }
}
export function download(value: unknown, name: string) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
const decode = (value: string) =>
  Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));
/** Re-checks the published letter in the browser against Traceira's public signing keys. */
export async function verifyLetter(letter: DomainLetter, keys: (JsonWebKey & { kid: string })[]) {
  const [header, payload, signature] = letter.jws.split('.');
  if (!header || !payload || !signature) return false;
  const h = JSON.parse(new TextDecoder().decode(decode(header))) as {
    alg: string;
    typ: string;
    kid: string;
  };
  if (h.alg !== 'Ed25519' || h.typ !== 'traceira-domain-authorization+jws') return false;
  const jwk = keys.find((k) => k.kid === h.kid);
  if (!jwk) return false;
  const claims = Object.fromEntries(Object.entries(letter).filter(([key]) => key !== 'jws'));
  if (JSON.stringify(claims) !== new TextDecoder().decode(decode(payload))) return false;
  const key = await crypto.subtle.importKey('jwk', jwk, { name: 'Ed25519' }, false, ['verify']);
  return crypto.subtle.verify(
    'Ed25519',
    key,
    decode(signature),
    new TextEncoder().encode(`${header}.${payload}`),
  );
}
